import * as React from 'react';
import {createViewConfig, view, assignProps} from '@sail/engine';
import type {View} from '../view';
import {useCurrentLayer} from './layers/Layer';
import {usePress} from './hooks/usePress';
import type {PressEvents} from './types';
import {useUiConfig} from '../providers/UiConfigProvider';

const isNestedOverlay = (
  layerContext: ReturnType<typeof useCurrentLayer> | null | undefined,
): boolean => {
  if (!layerContext) {
    return false;
  }

  if (layerContext.type === 'overlay') {
    return true;
  }

  return isNestedOverlay(layerContext.parent);
};

export type BackdropProps = View.IntrinsicElement<
  'div',
  Pick<PressEvents, 'onPress'> & {
    /**
     * @deprecated Use onPress instead of onClick
     * @ignore
     */
    onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  }
>;

export const BackdropConfig = createViewConfig({
  props: {} as BackdropProps,
  name: 'Backdrop',
  flattens: true,
});

const isAndroid =
  typeof navigator !== 'undefined' && /(android)/i.test(navigator.userAgent);

function useBackdropOpacity(
  layerContext: ReturnType<typeof useCurrentLayer>,
): number | undefined {
  const uiConfig = useUiConfig();
  const isUsingBottomSheets =
    uiConfig?.isSmallScreen &&
    uiConfig?.isTouch &&
    uiConfig?.enableBottomSheets;

  // For bottom sheets, always use full opacity
  if (isUsingBottomSheets) {
    return 1;
  }

  // For stacked Drawers, set opacity to 0 when in a nested layer
  if (isNestedOverlay(layerContext?.parent)) {
    return 0;
  }

  // By default, the opacity is undefined (will be set by parent component)
  return undefined;
}

export const Backdrop = BackdropConfig.createView(
  ({onPress, ...props}) => {
    const layerContext = useCurrentLayer();
    const ref = React.useRef<HTMLDivElement>(null);
    const opacity = useBackdropOpacity(layerContext);
    const {pressProps} = usePress({
      onPress: (event) => {
        // Temporary disabling backdrop closures on Android devices until we can fix the root
        // cause, which is onClick and onPress events interacting undesirably.
        !isAndroid && onPress?.(event);
      },
      ref,
    });

    return (
      <view.div
        data-testid="backdrop"
        ref={ref}
        uses={[assignProps(pressProps as View.IntrinsicElement<'div'>)]}
        css={{opacity}}
        {...props}
      />
    );
  },
  {
    css: {
      backgroundColor: 'backdrop',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    },
  },
);
